import { initializeApp } from 'firebase/app';
import { getMessaging, getToken } from "firebase/messaging";
import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useMemo, useReducer } from 'react';
import API from 'services/API';
import { FIREBASE_API, FIREBASE_VAPID_KEY } from '../config-global';

// utils
import axios from '../utils/axios';
//
import { setCsrfSession, setSession, clearServerSession } from './utils';
// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  pageSlug: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === 'AUTHENTICATION') {
    return {
      ...state,
      pageSlug: action.payload.pageSlug,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  async function requestPermission(user) {
    try {
      console.log('Requesting permission...');
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          console.log('Notification permission granted.');
          // WB-PP-05: Firebase web config + VAPID from env only (no hardcoded keys)
          if (!FIREBASE_API?.apiKey || !FIREBASE_VAPID_KEY) {
            console.warn('Firebase messaging skipped: REACT_APP_FIREBASE_* / VAPID not configured');
            return;
          }
          const firebaseApp = initializeApp(FIREBASE_API);
          const messaging = getMessaging(firebaseApp);
          getToken(messaging, { vapidKey: FIREBASE_VAPID_KEY }).then(async (currentToken) => {
            if (currentToken) {
              // Send the token to your server and update the UI if necessary
              // console.log(user, "currentToken=", currentToken);
              const payload = {
                userId: user?.websiteProjectId,
                token: currentToken,
              };
              // Cookie auth — no sessionStorage JWT
              const ress = await API.post(
                `/project-panel/fcm-configuration/register-fcm-token`,
                payload,
                {
                  headers: {
                    'Content-Type': 'application/json',
                  }
                }
              ); console.log("ress=", ress);
              // await API.post(
              //   `/project-panel/fcm-configuration/register-fcm-token`,
              //   payload
              // );
              // ...
            } else {
              // Show permission request UI
              console.log('No registration token available. Request permission to generate one.');
              // ...
            }
          }).catch((err) => {
            console.log('An error occurred while retrieving token. ', err);
            // ...
          });
          // }
        } else {
          console.log('do not have permission');
        }
      });
    } catch (error) {
      console.error('Error during permission request or FCM setup:', error);
    }
  }
  const initialize = useCallback(async () => {
    try {
      // WB-PP-01/02: restore via httpOnly cookie, not sessionStorage JWT
      sessionStorage.removeItem('accessToken');

      const csrfResponse = await API.get('/project-panel/csrf-token');
      const { csrfToken } = csrfResponse.data;
      setCsrfSession(csrfToken);
      setSession('cookie');

      const response = await API.get('/project-panel/auth/check-staff-user-refresh-token');
      const { user } = response.data.data;

      await requestPermission(user);
      sessionStorage.setItem('defaultPreset', user?.ownerDetails?.adminThemeColor);
      sessionStorage.setItem('bucketName', user?.s3BucketFolder || "maira-s3-bucket");
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: true,
          user,
        },
      });
    } catch (error) {
      console.error(error);
      setSession(null);
      setCsrfSession(null);
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Login Account
  const loginAccount = useCallback(async (user, token) => {
    if (token) {
      console.error('WB-PP-01: token leaked to browser — check /api/proxy BFF');
    }
    sessionStorage.setItem('defaultPreset', user?.ownerDetails?.adminThemeColor);

    setSession('cookie');

    // WB-PP-13: strip password hash from client state
    const { password: _omit, ...safeUser } = user || {};

    dispatch({
      type: 'LOGIN',
      payload: {
        user: safeUser,
      },
    });

    window.location.reload();
  }, []);
  // LOGIN
  const login = useCallback(async (username, password) => {
    const response = await API.post('/project-panel/auth/login-staff-with-password', {
      username,
      password,
    });
    const { token, websites, user } = response.data.data || {};
    if (token) {
      console.error('WB-PP-01: token leaked to browser — check /api/proxy BFF');
    }
    if (user) {
      await loginAccount(user, token);
      return { user, token: null };
    }
    setSession('cookie');
    return { websites: websites || [], token: null, user: user || null };
  }, [loginAccount]);
  // Select Account
  const selectAccount = useCallback(async (accId) => {
    const response = await API.get(
      `/project-panel/auth/login-website-project-panel-user/${accId}`
    );
    const { token, user } = response.data.data || {};
    if (token) {
      console.error('WB-PP-01: token leaked to browser — check /api/proxy BFF');
    }

    sessionStorage.setItem('defaultPreset', user?.ownerDetails?.adminThemeColor);

    setSession('cookie');

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });

    window.location.reload();
  }, []);

  // Switch Account
  const switchAccount = useCallback(async (accId) => {
    const response = await API.get(
      `/project-panel/auth/switch-website-project-panel-user/${accId}`
    );
    const { token, user } = response.data.data || {};
    if (token) {
      console.error('WB-PP-01: token leaked to browser — check /api/proxy BFF');
    }

    sessionStorage.setItem('defaultPreset', user?.ownerDetails?.adminThemeColor);

    setSession('cookie');

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });

    window.location.reload();
  }, []);
  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    // WB-PP-01: demo register path — never persist JWT in web storage
    setSession(accessToken || 'cookie');

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    try {
      await API.post('/project-panel/auth/logout');
    } catch (_) {
      /* still clear */
    }
    await clearServerSession();
    setCsrfSession(null);
    setSession(null);
    dispatch({
      type: 'LOGOUT',
    });
    sessionStorage.removeItem('defaultPreset');
  }, []);

  // AUTHENTICATION
  const setPageSlug = useCallback((slug = null) => {
    dispatch({
      type: 'AUTHENTICATION',
      payload: {
        pageSlug: slug,
      },
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      pageSlug: state.pageSlug,
      method: 'jwt',
      login,
      loginAccount,
      selectAccount,
      switchAccount,
      register,
      logout,
      setPageSlug,
    }),
    [
      state.isAuthenticated,
      state.isInitialized,
      state.user,
      state.pageSlug,
      login,
      loginAccount,
      selectAccount,
      switchAccount,
      logout,
      register,
      setPageSlug,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
